import React, { useState } from "react";
import Context from "../../../../Context";
import { useToast } from "../../../../components/ui";
import {
  X,
  CheckCircle2,
  XCircle,
  Phone,
  ShieldCheck,
  FileText,
  Eye,
  AlertTriangle,
} from "lucide-react";

export const TransferReviewModal = ({
  isOpen,
  onClose,
  request,
  onSuccess,
}) => {
  const { callApi, profileState } = Context();
  const { toast } = useToast();

  const [loading, setLoading] = useState(false);
  const [activeImageModal, setActiveImageModal] = useState(null);
  const [isRejecting, setIsRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  if (!isOpen || !request) return null;

  const currentRole = (profileState?.role || "").toLowerCase();
  const isAuthorized =
    currentRole === "dev" ||
    currentRole === "ceo" ||
    currentRole === "superadmin" ||
    profileState?.superAdmin ||
    profileState?.superSuperAdmin;

  const handleApprove = async () => {
    if (!window.confirm("ยืนยันการอนุมัติและโอนย้ายกรรมสิทธิ์คำสั่งซื้อนี้ใช่หรือไม่?")) {
      return;
    }

    setLoading(true);
    try {
      const result = await callApi("orders-approveTransfer", {
        requestId: request.id,
      });

      if (result?.status) {
        toast({
          className: "bg-emerald-600 text-white font-semibold py-2 px-3 rounded-md",
          title: (
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              <span>อนุมัติและโอนย้ายคำสั่งซื้อเรียบร้อยแล้ว</span>
            </div>
          ),
        });
        if (onSuccess) onSuccess();
        onClose();
      } else {
        toast({
          className: "bg-rose-600 text-white font-semibold py-2 px-3 rounded-md",
          title: result?.message || "ไม่สามารถอนุมัติได้",
        });
      }
    } catch (err) {
      console.error("Approve transfer error:", err);
      toast({
        className: "bg-rose-600 text-white font-semibold py-2 px-3 rounded-md",
        title: "เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      toast({
        className: "bg-rose-600 text-white font-semibold py-2 px-3 rounded-md",
        title: "กรุณาระบุเหตุผลการปฏิเสธคำขอ",
      });
      return;
    }

    setLoading(true);
    try {
      const result = await callApi("orders-rejectTransfer", {
        requestId: request.id,
        rejectReason: rejectReason.trim(),
      });

      if (result?.status) {
        toast({
          className: "bg-amber-600 text-white font-semibold py-2 px-3 rounded-md",
          title: "ปฏิเสธคำขอย้ายคำสั่งซื้อเรียบร้อยแล้ว",
        });
        if (onSuccess) onSuccess();
        onClose();
      } else {
        toast({
          className: "bg-rose-600 text-white font-semibold py-2 px-3 rounded-md",
          title: result?.message || "ไม่สามารถปฏิเสธคำขอได้",
        });
      }
    } catch (err) {
      console.error("Reject transfer error:", err);
      toast({
        className: "bg-rose-600 text-white font-semibold py-2 px-3 rounded-md",
        title: "เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์",
      });
    } finally {
      setLoading(false);
    }
  };

  const isPending = request.status === "pending";
  const isApproved = request.status === "approved";
  const isRejected = request.status === "rejected";

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 overflow-y-auto">
        <div className="bg-white w-full max-w-2xl rounded-md shadow-2xl border border-slate-200 overflow-hidden my-8 animate-in fade-in-50 zoom-in-95 duration-150">
          {/* Header */}
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/80">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue/10 text-blue rounded-md">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-bold text-slate-800">
                    ตรวจสอบคำขอย้ายคำสั่งซื้อ
                  </h3>
                  {isPending && (
                    <span className="px-2 py-0.5 text-xs font-bold rounded-md bg-amber-100 text-amber-800 border border-amber-200">
                      รอดำเนินการ
                    </span>
                  )}
                  {isApproved && (
                    <span className="px-2 py-0.5 text-xs font-bold rounded-md bg-emerald-100 text-emerald-800 border border-emerald-200">
                      อนุมัติแล้ว
                    </span>
                  )}
                  {isRejected && (
                    <span className="px-2 py-0.5 text-xs font-bold rounded-md bg-rose-100 text-rose-800 border border-rose-200">
                      ปฏิเสธแล้ว
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 font-medium">
                  รหัสคำสั่งซื้อ: <span className="font-mono font-bold text-slate-700">{request.orderId}</span>
                  {request.roundDate ? ` • งวด ${request.roundDate}` : ""}
                  {request.totalPrice ? ` • ${request.totalPrice.toLocaleString()} บาท` : ""}
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 rounded-md transition cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
            {/* Comparison Cards: เจ้าของเดิม ➡️ เจ้าของใหม่ */}
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                เปรียบเทียบการโอนย้ายกรรมสิทธิ์
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-stretch">
                {/* จากบัญชีเดิม */}
                <div className="bg-slate-50 p-3.5 rounded-md border border-slate-200">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block mb-1">
                    จากเจ้าของเดิม (From)
                  </span>
                  <p className="text-sm font-bold text-slate-800">
                    {request.fromUser?.firstName
                      ? `${request.fromUser.firstName} ${request.fromUser.lastName || ""}`
                      : "ไม่ระบุชื่อ"}
                  </p>
                  <p className="text-xs text-slate-500 font-mono mt-0.5 flex items-center gap-1">
                    <Phone className="w-3 h-3 text-slate-400" />
                    {request.fromUser?.phone || "ไม่ระบุเบอร์"}
                  </p>
                  <div className="mt-2 pt-2 border-t border-slate-200">
                    <span className="text-[10px] font-mono text-slate-400 block truncate">
                      UID: {request.fromUser?.userId}
                    </span>
                  </div>
                </div>

                {/* ไปยังบัญชีใหม่ */}
                <div className="bg-blue/5 p-3.5 rounded-md border border-blue/30">
                  <span className="text-[11px] font-bold text-blue uppercase tracking-wider block mb-1">
                    ไปยังเจ้าของใหม่ (To)
                  </span>
                  <p className="text-sm font-bold text-slate-900">
                    {request.toUser?.firstName
                      ? `${request.toUser.firstName} ${request.toUser.lastName || ""}`
                      : "ไม่ระบุชื่อ"}
                  </p>
                  <p className="text-xs text-slate-600 font-mono mt-0.5 flex items-center gap-1">
                    <Phone className="w-3 h-3 text-blue" />
                    {request.toUser?.phone || "ไม่ระบุเบอร์"}
                  </p>
                  <div className="mt-2 pt-2 border-t border-blue/20">
                    <span className="text-[10px] font-mono text-blue/80 block truncate">
                      UID: {request.toUser?.userId}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* เหตุผลและรายละเอียด */}
            <div className="bg-slate-50 p-4 rounded-md border border-slate-200 space-y-2">
              <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
                <FileText className="w-4 h-4 text-slate-500" />
                <span>สาเหตุ: {request.reason}</span>
              </div>
              {request.detail && (
                <p className="text-xs text-slate-600 bg-white p-2.5 rounded-md border border-slate-200">
                  {request.detail}
                </p>
              )}
              <div className="pt-2 flex flex-wrap items-center justify-between text-[11px] text-slate-500 gap-2 border-t border-slate-200/80">
                <span>
                  ผู้ส่งคำขอ: <strong className="text-slate-700">{request.requestedBy?.name || "แอดมิน"}</strong> ({request.requestedBy?.role || "admin"})
                </span>
                <span>
                  เวลาที่ยื่น: {request.createdAt ? new Date(request.createdAt).toLocaleString("th-TH") : "-"}
                </span>
              </div>
            </div>

            {/* รูปหลักฐานแนบ */}
            <div>
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">
                รูปหลักฐานแนบ ({Array.isArray(request.evidenceImages) ? request.evidenceImages.length : 0} รูป)
              </p>
              {Array.isArray(request.evidenceImages) && request.evidenceImages.length > 0 ? (
                <div className="flex flex-wrap gap-2.5">
                  {request.evidenceImages.map((imgUrl, idx) => (
                    <div
                      key={idx}
                      onClick={() => setActiveImageModal(imgUrl)}
                      className="relative w-24 h-24 rounded-md border border-slate-200 overflow-hidden bg-slate-100 group cursor-pointer shadow-2xs hover:shadow-md transition"
                    >
                      <img
                        src={imgUrl}
                        alt={`หลักฐาน ${idx + 1}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white">
                        <Eye className="w-5 h-5" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400 italic">ไม่มีรูปภาพหลักฐานแนบ</p>
              )}
            </div>

            {/* ประวัติการจัดการ (ถ้ามี) */}
            {isApproved && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-md text-xs text-emerald-900 font-medium">
                <p className="font-bold flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                  อนุมัติแล้วโดย {request.approvedBy?.name || "Dev"} ({request.approvedBy?.role || "dev"})
                </p>
                <p className="text-[11px] text-emerald-700 mt-0.5">
                  เวลาที่อนุมัติ: {request.approvedAt ? new Date(request.approvedAt).toLocaleString("th-TH") : "-"}
                </p>
              </div>
            )}

            {isRejected && (
              <div className="p-3 bg-rose-50 border border-rose-200 rounded-md text-xs text-rose-900 font-medium space-y-1">
                <p className="font-bold flex items-center gap-1.5">
                  <XCircle className="w-4 h-4 text-rose-600" />
                  ปฏิเสธแล้วโดย {request.rejectedBy?.name || "Dev"} ({request.rejectedBy?.role || "dev"})
                </p>
                <p className="text-[11px] text-rose-700">
                  เวลา: {request.rejectedAt ? new Date(request.rejectedAt).toLocaleString("th-TH") : "-"}
                </p>
                {request.rejectReason && (
                  <p className="text-xs bg-white/80 p-2 rounded border border-rose-200 mt-1">
                    เหตุผล: {request.rejectReason}
                  </p>
                )}
              </div>
            )}

            {/* ฟอร์มระบุเหตุผลเมื่อกดปฏิเสธ */}
            {isRejecting && (
              <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-md space-y-2 animate-in fade-in-50">
                <label className="text-xs font-bold text-rose-800 flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4 text-rose-600" />
                  <span>ระบุเหตุผลที่ปฏิเสธคำขอ:</span>
                </label>
                <textarea
                  rows={2}
                  placeholder="เช่น หลักฐานไม่เพียงพอ, สลากถูกรางวัลแล้ว, ข้อมูลบัญชีไม่ตรงกัน..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full p-2 text-xs font-medium text-slate-800 bg-white border border-rose-300 rounded-md focus:outline-none focus:border-rose-500"
                />
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setIsRejecting(false)}
                    className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-800 rounded-md"
                  >
                    ยกเลิก
                  </button>
                  <button
                    type="button"
                    disabled={loading || !rejectReason.trim()}
                    onClick={handleReject}
                    className="px-3 py-1.5 text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 rounded-md shadow-2xs transition disabled:opacity-50"
                  >
                    {loading ? "กำลังบันทึก..." : "ยืนยันปฏิเสธ"}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div className="px-6 py-3.5 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
            <button
              type="button"
              onClick={onClose}
              className="text-sm font-semibold text-slate-600 hover:text-slate-800 px-4 py-2 rounded-md hover:bg-slate-200/60 transition cursor-pointer"
            >
              ปิดหน้าต่าง
            </button>

            {isPending && isAuthorized && !isRejecting && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={loading}
                  onClick={() => setIsRejecting(true)}
                  className="py-2.5 px-4 text-sm font-bold rounded-md bg-rose-50 text-rose-700 hover:bg-rose-600 hover:text-white border border-rose-200 transition cursor-pointer disabled:opacity-50"
                >
                  ปฏิเสธคำขอ
                </button>
                <button
                  type="button"
                  disabled={loading}
                  onClick={handleApprove}
                  className="py-2.5 px-4 text-sm font-bold rounded-md bg-emerald-600 hover:bg-emerald-700 text-white shadow-xs transition flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      <span>กำลังอนุมัติ...</span>
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      <span>อนุมัติและย้ายคำสั่งซื้อ</span>
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Full Resolution Image Lightbox */}
      {activeImageModal && (
        <div
          onClick={() => setActiveImageModal(null)}
          className="fixed inset-0 z-60 bg-black/85 backdrop-blur-xs flex items-center justify-center p-4 cursor-zoom-out"
        >
          <div className="relative max-w-4xl max-h-[90vh] bg-transparent">
            <img
              src={activeImageModal}
              alt="ภาพหลักฐานขนาดเต็ม"
              className="max-w-full max-h-[85vh] object-contain rounded-md shadow-2xl"
            />
            <button
              type="button"
              onClick={() => setActiveImageModal(null)}
              className="absolute -top-10 right-0 p-2 text-white/80 hover:text-white transition"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default TransferReviewModal;
